// 戦略モードのスケジュール (適用日時 × 量 × 価格) の適用日時まわりの純関数
// (spec docs/specs/bidding/intraday-bidding.md AC-BIN-005b)。
//
// 適用日時は「前日 / 当日 + 時刻」 で入力する。 時間前市場は受渡日前日 17:00 開場〜
// 当日 GC で日を跨ぐため、 時刻だけでは前日 / 当日のどちらを指すか一意に決まらない。
// 入力の時刻は JST の壁時計として解釈する。
//
// 保存時の「開場時刻 ≤ 適用日時 < GC」 の検証は backend (IntradayOrderUsecase.Update)
// が正本で、 ここは入力段で同じ範囲を先に示すための UX 目的の事前チェック。
import {
  addDays,
  formatJstTimeHm,
  jstWallClockToInstant,
  jstYmd,
} from '~/models/dateTime'
import { calcGcAtForKoma } from '~/pages/IntradayBidding/models/gcCountdown'
import { marketOpenAt } from '~/pages/IntradayBidding/models/marketOpen'

/** 適用日時の日付側 (受渡日の前日 / 受渡日当日)。 */
export type ScheduleDay = 'dayBefore' | 'deliveryDay'

/** 適用日時の違反種別 (= 表示する message の出し分けに使う)。 */
export type ScheduleAppliedAtViolation = 'beforeMarketOpen' | 'atOrAfterGc'

const TIME_HM_PATTERN = /^(\d{2}):(\d{2})$/

/**
 * 「前日 / 当日 + 時刻 (`<input type="time">` の "HH:mm")」 を JST 壁時計として
 * 絶対時刻に解決する。 時刻が未入力 / 形式不正なら `undefined`。
 *
 * @param deliveryDate 受渡日 (= カレンダー日 `Date`、 時刻部分は参照しない)
 */
export const resolveScheduleAppliedAt = ({
  day,
  timeHm,
  deliveryDate,
}: {
  day: ScheduleDay
  timeHm: string
  deliveryDate: Date
}): Date | undefined => {
  const m = TIME_HM_PATTERN.exec(timeHm)
  if (m === null) return undefined
  const hour = Number(m[1])
  const minute = Number(m[2])
  if (hour > 23 || minute > 59) return undefined
  const calendarDate =
    day === 'dayBefore' ? addDays(deliveryDate, -1) : deliveryDate
  return jstWallClockToInstant(calendarDate, hour, minute)
}

/**
 * 適用日時が「開場時刻 (受渡日前日 17:00 JST) ≤ 適用日時 < 対象コマの GC」 に
 * 収まるかを検証し、 外れていればその種別を返す (収まっていれば `undefined`)。
 *
 * 範囲は半開区間: 開場ちょうどは適用でき、 GC ちょうどは板の札が終了する瞬間なので
 * 適用されない (backend `IntradayOrder.IsWithinScheduleWindow` と同じ境界)。
 */
export const validateScheduleAppliedAt = ({
  appliedAt,
  deliveryDate,
  koma,
}: {
  appliedAt: Date
  deliveryDate: Date
  koma: number
}): ScheduleAppliedAtViolation | undefined => {
  if (appliedAt.getTime() < marketOpenAt(deliveryDate).getTime()) {
    return 'beforeMarketOpen'
  }
  if (appliedAt.getTime() >= calcGcAtForKoma(deliveryDate, koma).getTime()) {
    return 'atOrAfterGc'
  }
  return undefined
}

/**
 * 保存済みの適用日時 (絶対時刻) を入力欄の「前日 / 当日 + HH:mm」 に戻す。
 * 受渡日の前日・当日のどちらでもない (= 取引窓の外。 backend が保存を拒否するので
 * 通常は起きない) 場合は `undefined`。
 */
export const toScheduleDayAndTime = (
  appliedAt: Date,
  deliveryDate: Date,
): { day: ScheduleDay; timeHm: string } | undefined => {
  const at = jstYmd(appliedAt)
  const sameYmd = (d: Date): boolean =>
    at.year === d.getFullYear() &&
    at.month === d.getMonth() + 1 &&
    at.day === d.getDate()
  const timeHm = formatJstTimeHm(appliedAt)
  if (sameYmd(deliveryDate)) return { day: 'deliveryDay', timeHm }
  if (sameYmd(addDays(deliveryDate, -1))) return { day: 'dayBefore', timeHm }
  return undefined
}

/**
 * 適用日時が GC の何分前か (端数切り捨て)。 GC 以降は 0 以下になる。
 * 「GC の {N}分前」 表記 (AC-BIN-005b、 bid-limits.md AC-BDL-502 と表現統一) の元値。
 */
export const minutesBeforeGc = (appliedAt: Date, gcAt: Date): number =>
  Math.floor((gcAt.getTime() - appliedAt.getTime()) / 60_000)
